/**
 * Small, dependency-free CSV export helper used across list pages.
 *
 * Design rules (see product-level export requirements):
 *   • Never include secrets, tokens, signed URLs, storage paths, or IDs that
 *     would only be useful to the internal system. Only export data the user
 *     already sees on the page.
 *   • Respect the current filtered view — callers pass the ALREADY-filtered
 *     rows.
 *   • Excel-friendly: prepend a BOM so it opens correctly with double-click.
 *   • Escape everything as a string; wrap in quotes; double-up embedded
 *     quote characters. Newlines inside cells are allowed and preserved.
 */

const _fmtCell = (v) => {
  if (v == null) return "";
  if (v instanceof Date) return v.toISOString();
  if (Array.isArray(v)) return v.join("; ");
  if (typeof v === "object") {
    // Deliberate flattening — never dump raw JSON blobs (they can leak
    // internal keys, tokens, etc). Only shallow-render string/number values.
    return Object.entries(v)
      .filter(([, val]) => val != null && (typeof val === "string" || typeof val === "number"))
      .map(([k, val]) => `${k}: ${val}`)
      .join(" · ");
  }
  return String(v);
};

const _escape = (v) => {
  const s = _fmtCell(v);
  // Always quote — safer + Excel-friendly for values with commas/quotes/newlines
  return `"${s.replace(/"/g, '""')}"`;
};

/**
 * Convert rows to a CSV string.
 * @param {Array<{key: string, label: string, get?: (row:any)=>any}>} columns
 * @param {Array<any>} rows
 */
export function toCsv(columns, rows) {
  const header = columns.map((c) => _escape(c.label)).join(",");
  const body = rows.map((r) =>
    columns.map((c) => _escape(c.get ? c.get(r) : r?.[c.key])).join(",")
  ).join("\r\n");
  // \ufeff BOM so Excel auto-detects UTF-8.
  return `\ufeff${header}\r\n${body}`;
}

/**
 * Trigger a browser download of a CSV file built from rows.
 */
export function downloadCsv(filename, columns, rows) {
  const csv = toCsv(columns, rows);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".csv") ? filename : `${filename}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Format a Date-ish value into an ISO date string (YYYY-MM-DD).
 */
export function iso(d) {
  if (!d) return "";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toISOString().slice(0, 10);
}

/**
 * Cents → "$1,234.56".
 */
export function money(cents, currency = "USD") {
  if (cents == null) return "";
  return `${currency.toUpperCase() === "USD" ? "$" : ""}${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
